import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { formatDateSafe } from '../../../utils/dateUtils';
import {
  Eye, 
  Edit2, 
  Users, 
  MoreHorizontal, 
  Search,
  Filter,
  Download,
  Plus,
  ChevronLeft,
  ChevronRight,
  Activity,
  AlertCircle
} from 'lucide-react';
import type { 
  EmpresaListItem, 
  EmpresasFiltros,
  PaginatedResponse 
} from '../../../types';

interface EmpresasTableProps {
  data: PaginatedResponse<EmpresaListItem>;
  filtros: EmpresasFiltros;
  loading: boolean;
  onFiltrosChange: (filtros: EmpresasFiltros) => void;
  onVerEmpresa: (empresa: EmpresaListItem) => void;
  onEditarEmpresa: (empresa: EmpresaListItem) => void;
  onGestionarUsuarios: (empresa: EmpresaListItem) => void;
  onNuevaEmpresa: () => void;
  onExportar: () => void;
}

const EmpresasTable: React.FC<EmpresasTableProps> = ({
  data,
  filtros,
  loading,
  onFiltrosChange,
  onVerEmpresa,
  onEditarEmpresa,
  onGestionarUsuarios,
  onNuevaEmpresa,
  onExportar
}) => {
  const { t } = useTranslation();
  const [busquedaLocal, setBusquedaLocal] = useState(filtros.busqueda || '');
  const [mostrarFiltros, setMostrarFiltros] = useState(false);

  const handleBusquedaSubmit = (e: React.FormEvent) => {
    e.preventDefault();
    onFiltrosChange({ ...filtros, busqueda: busquedaLocal, page: 1 });
  };

  const handleFiltroEstado = (es_activa?: boolean) => {
    onFiltrosChange({ ...filtros, es_activa, page: 1 });
  };

  const handleOrden = (orden: 'nombre' | 'rut' | 'created_at') => {
    const nuevaDireccion = filtros.orden === orden && filtros.direccion === 'asc' ? 'desc' : 'asc';
    onFiltrosChange({ ...filtros, orden, direccion: nuevaDireccion, page: 1 });
  };

  const handlePaginacion = (page: number) => {
    onFiltrosChange({ ...filtros, page });
  };

  return (
    <div className="bg-white rounded-lg shadow-sm border border-gray-200">
      <div className="p-6 border-b border-gray-200">
        <div className="flex flex-col lg:flex-row lg:items-center lg:justify-between gap-4">
          <div>
            <h2 className="text-xl font-semibold text-gray-900">
              {t('admin.companies')}
            </h2>
            <p className="text-sm text-gray-600 mt-1">
              Administra empresas, usuarios y permisos del sistema MAVA
            </p>
          </div>
          
          <div className="flex flex-col sm:flex-row gap-3">
            <button
              onClick={() => {
                onExportar();
              }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50"
            >
              <Download className="w-4 h-4 mr-2" />
              {t('common.export')}
            </button>
            
            <button
              onClick={() => {
                onNuevaEmpresa();
              }}
              className="inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
            >
              <Plus className="w-4 h-4 mr-2" />
              {t('admin.add')} {t('admin.companies')}
            </button>
          </div>
        </div>

        <div className="mt-4 flex flex-col lg:flex-row gap-4">
          <form onSubmit={handleBusquedaSubmit} className="flex-1">
            <div className="relative">
              <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 text-gray-400 w-4 h-4" />
              <input
                type="text"
                placeholder={t('admin.search_placeholder')}
                value={busquedaLocal}
                onChange={(e) => setBusquedaLocal(e.target.value)}
                className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-md focus:ring-green-500 focus:border-green-500"
              />
            </div>
          </form>
          
          <div className="flex gap-2">
            <button
              onClick={() => setMostrarFiltros(!mostrarFiltros)}
              className={`inline-flex items-center px-3 py-2 text-sm font-medium border rounded-md ${
                mostrarFiltros
                  ? 'text-green-700 bg-green-50 border-green-300'
                  : 'text-gray-700 bg-white border-gray-300 hover:bg-gray-50'
              }`}
            >
              <Filter className="w-4 h-4 mr-2" />
              {t('analysis.filters')}
            </button>
          </div>
        </div>

        {mostrarFiltros && (
          <div className="mt-4 p-4 bg-gray-50 rounded-md border border-gray-200">
            <div className="flex flex-wrap gap-4">
              <div className="flex gap-2">
                <span className="text-sm font-medium text-gray-700">{t('common.status')}:</span>
                <button
                  onClick={() => handleFiltroEstado(undefined)}
                  className={`px-3 py-1 text-xs rounded-full border ${
                    filtros.es_activa === undefined
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('common.all')}
                </button>
                <button
                  onClick={() => handleFiltroEstado(true)}
                  className={`px-3 py-1 text-xs rounded-full border ${
                    filtros.es_activa === true
                      ? 'bg-green-100 text-green-800 border-green-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('admin.status_active')}
                </button>
                <button
                  onClick={() => handleFiltroEstado(false)}
                  className={`px-3 py-1 text-xs rounded-full border ${
                    filtros.es_activa === false
                      ? 'bg-red-100 text-red-800 border-red-300'
                      : 'bg-white text-gray-600 border-gray-300 hover:bg-gray-50'
                  }`}
                >
                  {t('admin.status_inactive')}
                </button>
              </div>
            </div>
          </div>
        )}
      </div>

      <div className="overflow-x-auto">
        <table className="min-w-full divide-y divide-gray-200">
          <thead className="bg-gray-50">
            <tr>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleOrden('nombre')}
              >
                <div className="flex items-center">
                  {t('admin.companies')}
                  {filtros.orden === 'nombre' && (
                    <span className="ml-1">
                      {filtros.direccion === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th 
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleOrden('rut')}
              >
                <div className="flex items-center">
                  RUT
                  {filtros.orden === 'rut' && (
                    <span className="ml-1">
                      {filtros.direccion === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.status')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('admin.title')}
              </th>
              <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider">
                {t('common.details')}
              </th>
              <th
                className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider cursor-pointer hover:bg-gray-100"
                onClick={() => handleOrden('created_at')}
              >
                <div className="flex items-center">
                  {t('common.date')}
                  {filtros.orden === 'created_at' && (
                    <span className="ml-1">
                      {filtros.direccion === 'asc' ? '↑' : '↓'}
                    </span>
                  )}
                </div>
              </th>
              <th className="relative px-6 py-3">
                <span className="sr-only">{t('common.actions')}</span>
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {loading ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-green-600"></div>
                    <p className="mt-2 text-sm text-gray-500">{t('common.loading')}</p>
                  </div>
                </td>
              </tr>
            ) : data.items.length === 0 ? (
              <tr>
                <td colSpan={7} className="px-6 py-12 text-center">
                  <div className="flex flex-col items-center">
                    <AlertCircle className="h-12 w-12 text-gray-400" />
                    <h3 className="mt-2 text-sm font-medium text-gray-900">{t('common.no_data')}</h3>
                    <p className="mt-1 text-sm text-gray-500">
                      {filtros.busqueda || filtros.es_activa !== undefined
                        ? t('common.no_results')
                        : t('common.no_data')}
                    </p>
                    {(!filtros.busqueda && filtros.es_activa === undefined) && (
                      <button
                        onClick={onNuevaEmpresa}
                        className="mt-3 inline-flex items-center px-4 py-2 text-sm font-medium text-white bg-green-600 border border-transparent rounded-md hover:bg-green-700"
                      >
                        <Plus className="w-4 h-4 mr-2" />
                        {t('common.create')} {t('admin.companies')}
                      </button>
                    )}
                  </div>
                </td>
              </tr>
            ) : (
              data.items.map((empresa) => (
                <tr key={empresa.id} className="hover:bg-gray-50">
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div>
                      <div className="text-sm font-medium text-gray-900">
                        {empresa.nombre}
                      </div>
                      <div className="text-sm text-gray-500">
                        {empresa.razon_social}
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">{empresa.rut}</div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <span
                      className={`inline-flex px-2 py-1 text-xs font-medium rounded-full ${
                        empresa.es_activa
                          ? 'bg-green-100 text-green-800'
                          : 'bg-red-100 text-red-800'
                      }`}
                    >
                      <Activity className="w-3 h-3 mr-1" />
                      {empresa.es_activa ? t('admin.status_active') : t('admin.status_inactive')}
                    </span>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="flex items-center">
                      <Users className="w-4 h-4 text-gray-400 mr-1" />
                      <span className="text-sm text-gray-900">{empresa.usuarios_count}</span>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      <div className="flex items-center space-x-4">
                        <span title="Maquinas">
                          M: {empresa.recursos_count.maquinas}
                        </span>
                        <span title="Operadores">
                          O: {empresa.recursos_count.operadores}
                        </span>
                        <span title="Faenas">
                          F: {empresa.recursos_count.faenas}
                        </span>
                        <span title="Reportes">
                          R: {empresa.recursos_count.reportes}
                        </span>
                      </div>
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap">
                    <div className="text-sm text-gray-900">
                      {formatDateSafe(empresa.created_at)}
                    </div>
                  </td>
                  <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium">
                    <div className="flex items-center justify-end space-x-2">
                      <button
                        onClick={() => {
                          onVerEmpresa(empresa);
                        }}
                        className="text-gray-600 hover:text-gray-900 p-1 rounded"
                        title="Ver detalles"
                      >
                        <Eye className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          onEditarEmpresa(empresa);
                        }}
                        className="text-blue-600 hover:text-blue-900 p-1 rounded"
                        title="Editar empresa"
                      >
                        <Edit2 className="w-4 h-4" />
                      </button>
                      <button
                        onClick={() => {
                          onGestionarUsuarios(empresa);
                        }}
                        className="text-green-600 hover:text-green-900 p-1 rounded"
                        title="Gestionar usuarios"
                      >
                        <Users className="w-4 h-4" />
                      </button>
                      <button
                        className="text-gray-400 hover:text-gray-600 p-1 rounded"
                        title="Mas opciones"
                      >
                        <MoreHorizontal className="w-4 h-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))
            )}
          </tbody>
        </table>
      </div>

      {data.items.length > 0 && (
        <div className="px-6 py-3 bg-gray-50 border-t border-gray-200">
          <div className="flex items-center justify-between">
            <div className="flex items-center text-sm text-gray-700">
              <span>
                {t('common.showing')} {(data.page - 1) * data.per_page + 1} a{' '}
                {Math.min(data.page * data.per_page, data.total)} {t('common.of')} {data.total} {t('admin.companies')}
              </span>
            </div>
            
            <div className="flex items-center space-x-2">
              <button
                onClick={() => handlePaginacion(data.page - 1)}
                disabled={!data.has_prev}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronLeft className="w-4 h-4" />
              </button>
              
              <div className="flex items-center space-x-1">
                {Array.from({ length: Math.min(5, data.pages) }, (_, i) => {
                  const pageNum = Math.max(1, data.page - 2) + i;
                  if (pageNum > data.pages) return null;
                  
                  return (
                    <button
                      key={pageNum}
                      onClick={() => handlePaginacion(pageNum)}
                      className={`px-3 py-2 text-sm font-medium rounded-md ${
                        pageNum === data.page
                          ? 'bg-green-600 text-white'
                          : 'text-gray-700 bg-white border border-gray-300 hover:bg-gray-50'
                      }`}
                    >
                      {pageNum}
                    </button>
                  );
                })}
              </div>
              
              <button
                onClick={() => handlePaginacion(data.page + 1)}
                disabled={!data.has_next}
                className="inline-flex items-center px-3 py-2 text-sm font-medium text-gray-500 bg-white border border-gray-300 rounded-md hover:bg-gray-50 disabled:opacity-50 disabled:cursor-not-allowed"
              >
                <ChevronRight className="w-4 h-4" />
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default EmpresasTable;
